import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'

/**
 * Calculates the Net Promoter Score (NPS) from Net Promoter responses
 *
 * NPS Calculation:
 * - Promoters: Customers who gave a score of 9 or 10
 * - Passives: Customers who gave a score of 7 or 8 (not included in calculation)
 * - Detractors: Customers who gave a score of 0-6
 * - NPS = (Percentage of Promoters) - (Percentage of Detractors)
 *
 * @param responses - Array of response rows, filtered to only Net Promoter questions
 * @returns The NPS score (ranges from -100 to +100), or null if no valid responses
 */
export const calculateNPS = (
  responses: IBroadcastFormRecipientResponseReportRow[],
): number | null => {
  // Filter to only Net Promoter responses with valid OptionValue
  const npsResponses = responses.filter(
    (row) => row.QuestionCategory === 'Net Promoter'
      && row.OptionValue != null
      && row.OptionValue >= 0
      && row.OptionValue <= 10,
  )

  if (npsResponses.length === 0) {
    return null
  }

  // Categorize responses
  let promoters = 0 // 9-10
  let detractors = 0 // 0-6
  // Passives (7-8) are counted in total but not used in calculation

  npsResponses.forEach((row) => {
    const value = row.OptionValue!
    if (value >= 9 && value <= 10) {
      promoters += 1
    } else if (value >= 0 && value <= 6) {
      detractors += 1
    }
    // Passives (7-8) are ignored
  })

  const total = npsResponses.length

  // Calculate percentages
  const promoterPercentage = (promoters / total) * 100
  const detractorPercentage = (detractors / total) * 100

  // Calculate NPS
  const nps = promoterPercentage - detractorPercentage

  // Round to nearest integer
  return Math.round(nps)
}
